import { useMemo } from 'react';
import { Sparkles, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/hooks/useCollection';
import { useDecks } from '@/hooks/useDecks';
import { useWishlist } from '@/hooks/useWishlist';

type Surface = 'home' | 'gallery' | 'decks';

type Tip = {
  text: string;
  hash?: string; // navigate via location.hash
  onClick?: () => void;
};

interface Props {
  surface: Surface;
  onNavigate?: (tabId: string, subTab?: string) => void;
}

/**
 * Compact, surface-aware suggestion strip driven by profile.play_style + goals.
 * Falls back gracefully when profile data isn't available.
 */
export function PersonalizedSuggestions({ surface, onNavigate }: Props) {
  const { profile } = useAuth();
  const { data: collection } = useCollection();
  const { data: decks } = useDecks();
  const { data: wishlist } = useWishlist();

  const tips = useMemo<Tip[]>(() => {
    if (!profile) return [];
    const style: string = (profile as any).play_style ?? '';
    const goals: string[] = (profile as any).goals ?? [];
    const list: Tip[] = [];
    const totalCards = (collection || []).reduce((s, c) => s + (c.quantity || 1), 0);
    const deckCount = decks?.length ?? 0;
    const legalDecks = (decks || []).filter((d: any) => (d.total_cards ?? 0) === 60).length;
    const illegal = (decks || []).find((d: any) => (d.total_cards ?? 0) > 0 && d.total_cards !== 60);

    const nav = (tab: string, subTab?: string): Tip['onClick'] => () => {
      if (onNavigate) return onNavigate(tab, subTab);
      const parts = [tab, subTab].filter(Boolean).join('/');
      window.location.hash = `#${parts}`;
    };

    // Surface-specific hero suggestion
    if (surface === 'decks') {
      if (illegal) {
        list.push({
          text: `Finish "${illegal.name}" (${(illegal as any).total_cards}/60)`,
          onClick: nav('collection', 'decks'),
        });
      } else if (deckCount === 0) {
        list.push({ text: 'Build your first deck', onClick: nav('collection', 'decks') });
      }
      if (goals.includes('win_event')) {
        list.push({ text: 'Try a meta deck from Decks of the Week', onClick: () => (window.location.href = '/decks/top') });
      }
    }

    if (surface === 'gallery') {
      if (totalCards === 0) {
        list.push({ text: 'Scan your first card to showcase it', onClick: nav('collection', 'collection') });
      } else {
        list.push({ text: 'Showcase your favorite pull', onClick: nav('community') });
      }
      if (goals.includes('trade') && (wishlist?.length ?? 0) > 0) {
        list.push({ text: `Find traders for ${wishlist!.length} wishlist cards`, onClick: nav('community') });
      }
    }

    if (surface === 'home') {
      if (style === 'newcomer' || goals.includes('learn_rules')) {
        list.push({ text: 'Learn the basics in 5 minutes', onClick: nav('learn') });
      }
      if (goals.includes('build_deck') && deckCount === 0) {
        list.push({ text: 'Start your first deck', onClick: nav('collection', 'decks') });
      }
      if (goals.includes('complete_set')) {
        list.push({ text: 'Track set completion', onClick: nav('collection', 'sets') });
      }
      if (style === 'competitive' && legalDecks > 0) {
        list.push({ text: 'Log a match with your deck', onClick: nav('profile') });
      }
      if (style === 'collector' || goals.includes('track_value')) {
        list.push({ text: 'See collection insights & value', onClick: nav('collection', 'collection') });
      }
    }

    // Always-on fallback so the strip never feels empty.
    if (list.length === 0) {
      list.push({ text: 'Ask Scout for ideas', onClick: () => window.dispatchEvent(new CustomEvent('lorescout:open-scout')) });
    }
    return list.slice(0, 3);
  }, [profile, surface, collection, decks, wishlist, onNavigate]);

  if (!profile || tips.length === 0) return null;

  return (
    <div className="mt-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="text-[11px] font-semibold text-primary font-body uppercase tracking-wide">For You</span>
      </div>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
        {tips.map((t, i) => (
          <button
            key={i}
            onClick={t.onClick}
            className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-card border border-border px-3 py-1.5 text-[11px] text-foreground font-body hover:border-primary/40 transition-colors"
          >
            {t.text}
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
